/**
 * Portal export filenames and letterhead job-line text.
 * Patterns come from Admin → Settings → Nomenclature (core.nomenclature_rules).
 */

export const DEFAULT_NOMENCLATURE = {
  filename_pattern: '{job}_{report_slug}_{yyyy-mm-dd}.{ext}',
  job_line_pattern: '{job} - {client} - {job_name}',
  estimate_folder_pattern: '{yy}-{region}-####',
  sharepoint_job_site_pattern: '{job}{disc} {Name}',
  project_types: 'Lump Sum,T&M,Unit Price,Cost Plus',
  p6_weekly_pattern: '{estimate}-YYMMDD',
  p6_scenario_pattern: '{estimate}-{P}-{D}X{H}',
  p6_resource_id_pattern: '{estimateKey}-{TRADE}[-FAB][-EX]',
  p6_resource_name_pattern: '{Trade}[ Fabrication][ Extra]',
};

let cachedRules = null;

export function parseProjectTypes(value) {
  return String(value || DEFAULT_NOMENCLATURE.project_types)
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

export function displayProjectType(projectType) {
  const t = String(projectType || '').trim();
  return t || '...';
}

export function sanitizeSlug(value) {
  return String(value || 'export')
    .trim()
    .replace(/[^\w.-]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '') || 'export';
}

function isoDate(date) {
  const d = date instanceof Date ? date : new Date(date || Date.now());
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

export function jobLine({ job, client, jobName, pattern } = {}) {
  const jobPart = String(job || '').trim();
  const clientPart = String(client || '').trim();
  let namePart = String(jobName || '').trim();
  if (!jobPart && !namePart) return '';
  if (clientPart) {
    // Many job names already lead with the client ("Wieland - Domtar (TN) ...").
    // Strip that prefix so the letterhead never reads "Wieland - Wieland - ...".
    if (namePart.toLowerCase().startsWith(clientPart.toLowerCase())) {
      const rest = namePart.slice(clientPart.length).replace(/^[\s—–-]+/, '');
      if (rest) {
        namePart = rest;
      } else if (jobPart) {
        // Job name IS the client name — print it once.
        return `${jobPart} - ${namePart}`;
      }
    }
    const tmpl = pattern || cachedRules?.job_line_pattern || DEFAULT_NOMENCLATURE.job_line_pattern;
    return tmpl
      .replace('{job}', jobPart)
      .replace('{client}', clientPart)
      .replace('{job_name}', namePart);
  }
  if (jobPart && namePart) return `${jobPart} - ${namePart}`;
  return jobPart || namePart;
}

export function exportFilename({ job, reportSlug, ext, date, pattern } = {}) {
  const tokens = {
    '{job}': sanitizeSlug(job || 'portal'),
    '{report_slug}': sanitizeSlug(reportSlug),
    '{yyyy-mm-dd}': isoDate(date),
    '{ext}': String(ext || 'pdf').replace(/^\./, ''),
  };
  let name = pattern || cachedRules?.filename_pattern || DEFAULT_NOMENCLATURE.filename_pattern;
  Object.entries(tokens).forEach(([k, v]) => {
    name = name.split(k).join(v);
  });
  return name;
}

export function setNomenclatureCache(rules) {
  cachedRules = rules || null;
}

export function getNomenclatureCache() {
  return cachedRules;
}

/**
 * Load nomenclature rules (cached for the session).
 * apiCall is (method, url, data?) => promise of axios response.
 */
export async function loadNomenclature(apiCall) {
  if (cachedRules) return cachedRules;
  try {
    const res = await apiCall('GET', '/nomenclature');
    const data = res.data?.data;
    cachedRules = data?.rules || DEFAULT_NOMENCLATURE;
    return cachedRules;
  } catch (err) {
    console.warn('nomenclature: using defaults', err?.message);
    cachedRules = { ...DEFAULT_NOMENCLATURE };
    return cachedRules;
  }
}

export function projectTypeList(rules) {
  return parseProjectTypes(rules?.project_types || cachedRules?.project_types);
}
